'use client';

import { useState } from 'react';
import {
  FacebookShare,
  TwitterShare,
  LinkedinShare,
  WhatsappShare,
  EmailShare,
} from 'react-share-kit';
import { trackEvent } from '@/lib/analytics';

interface SocialShareProps {
  url: string;
  title: string;
  description?: string;
  locale?: string;
}

export default function SocialShare({ url, title, description, locale = 'pl' }: SocialShareProps) {
  const [copied, setCopied] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  const fullUrl = url.startsWith('http') ? url : `https://bikeparkmapa.pl${url}`;

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: description,
          url: fullUrl,
        });
        trackEvent({
          eventName: 'social_share',
          eventCategory: 'engagement',
          metadata: { method: 'native', url: fullUrl },
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      trackEvent({
        eventName: 'social_share',
        eventCategory: 'engagement',
        metadata: { method: 'copy_link', url: fullUrl },
      });
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShareClick = (platform: string) => {
    trackEvent({
      eventName: 'social_share',
      eventCategory: 'engagement',
      metadata: { method: platform, url: fullUrl },
    });
  };

  const translations = {
    pl: {
      share: 'Udostępnij',
      copyLink: 'Kopiuj link',
      copied: 'Skopiowano!',
      shareVia: 'Udostępnij przez:',
    },
    en: {
      share: 'Share',
      copyLink: 'Copy link',
      copied: 'Copied!',
      shareVia: 'Share via:',
    },
  };

  const t = translations[locale as keyof typeof translations] || translations.pl;

  return (
    <div className="social-share">
      {/* Main Share Button */}
      <button
        onClick={() => {
          if (navigator.share) {
            handleNativeShare();
          } else {
            setShowShareMenu(!showShareMenu);
          }
        }}
        className="share-button"
        title={t.share}
        aria-label={t.share}
      >
        <svg
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
          />
        </svg>
      </button>

      {/* Share Menu */}
      {showShareMenu && (
        <>
          {/* Backdrop */}
          <div
            className="share-backdrop"
            onClick={() => setShowShareMenu(false)}
          />

          {/* Menu */}
          <div className="share-menu">
            <div className="share-menu-header">
              {t.shareVia}
            </div>

            <div className="share-menu-items">
              {/* Copy Link */}
              <button
                onClick={handleCopyLink}
                className="share-menu-item"
              >
                <div className="share-icon-wrapper copy">
                  {copied ? (
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  )}
                </div>
                <span className="share-label">
                  {copied ? t.copied : t.copyLink}
                </span>
              </button>

              {/* Facebook */}
              <div className="share-menu-item-wrapper">
                <div onClick={() => handleShareClick('facebook')} style={{ display: 'contents' }}>
                  <FacebookShare
                    url={fullUrl}
                    quote={title}
                    size={36}
                    round
                  />
                </div>
                <span className="share-label">Facebook</span>
              </div>

              {/* Twitter / X */}
              <div className="share-menu-item-wrapper">
                <div onClick={() => handleShareClick('twitter')} style={{ display: 'contents' }}>
                  <TwitterShare
                    url={fullUrl}
                    title={title}
                    size={36}
                    round
                  />
                </div>
                <span className="share-label">Twitter / X</span>
              </div>

              {/* WhatsApp */}
              <div className="share-menu-item-wrapper">
                <div onClick={() => handleShareClick('whatsapp')} style={{ display: 'contents' }}>
                  <WhatsappShare
                    url={fullUrl}
                    title={title}
                    separator=" - "
                    size={36}
                    round
                  />
                </div>
                <span className="share-label">WhatsApp</span>
              </div>

              {/* LinkedIn */}
              <div className="share-menu-item-wrapper">
                <div onClick={() => handleShareClick('linkedin')} style={{ display: 'contents' }}>
                  <LinkedinShare
                    url={fullUrl}
                    title={title}
                    size={36}
                    round
                  />
                </div>
                <span className="share-label">LinkedIn</span>
              </div>

              {/* Email */}
              <div className="share-menu-item-wrapper">
                <div onClick={() => handleShareClick('email')} style={{ display: 'contents' }}>
                  <EmailShare
                    url={fullUrl}
                    subject={title}
                    body={description || ''}
                    size={36}
                    round
                  />
                </div>
                <span className="share-label">Email</span>
              </div>
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        .social-share {
          position: relative;
        }

        .share-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 10px;
          background: rgba(255, 255, 255, 0.15);
          border: 2px solid rgba(255, 255, 255, 0.25);
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          color: #ffffff;
          backdrop-filter: blur(8px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .share-button:hover {
          background: rgba(255, 255, 255, 0.22);
          border-color: rgba(255, 255, 255, 0.35);
          transform: translateY(-2px) scale(1.05);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
        }

        .share-button:active {
          transform: translateY(0) scale(0.95);
        }

        .share-button svg {
          width: 22px;
          height: 22px;
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
        }

        .share-backdrop {
          position: fixed;
          inset: 0;
          z-index: 40;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
        }

        .share-menu {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(12px);
          border: 2px solid rgba(102, 126, 234, 0.2);
          border-radius: 12px;
          padding: 20px;
          z-index: 50;
          min-width: 280px;
          max-width: 320px;
          box-shadow:
            0 8px 24px rgba(0, 0, 0, 0.12),
            0 0 0 1px rgba(102, 126, 234, 0.1);
        }

        .share-menu-header {
          font-size: 0.875rem;
          font-weight: 600;
          color: #4b5563;
          margin-bottom: 12px;
          padding: 0 4px;
          letter-spacing: 0.02em;
        }

        .share-menu-items {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .share-menu-item,
        .share-menu-item-wrapper {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 12px 14px;
          background: transparent;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          text-decoration: none;
          color: #1f2937;
          width: 100%;
          text-align: left;
          position: relative;
          overflow: hidden;
        }

        .share-menu-item::before,
        .share-menu-item-wrapper::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .share-menu-item:hover,
        .share-menu-item-wrapper:hover {
          background: rgba(102, 126, 234, 0.05);
          transform: translateX(4px);
        }

        .share-menu-item:hover::before,
        .share-menu-item-wrapper:hover::before {
          opacity: 1;
        }

        .share-icon-wrapper {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.2s ease;
        }

        .share-menu-item:hover .share-icon-wrapper,
        .share-menu-item-wrapper:hover :global(button) {
          transform: scale(1.1);
        }

        .share-icon-wrapper.copy {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .share-label {
          font-weight: 600;
          font-size: 0.9375rem;
          color: #374151;
          position: relative;
          z-index: 1;
        }
      `}</style>
    </div>
  );
}
